import { memo } from "react";

import type { ChartData, ChartOptions } from "chart.js/auto";
// eslint-disable-next-line no-duplicate-imports
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  registerables,
} from "chart.js";
import "chartjs-adapter-date-fns";

import { Line } from "react-chartjs-2";

import type { Locale } from "../types";
import localization from "../localization";
import chartKeyboardNavPlugin from "../plugins/ChartKeyboardNavPlugin";
import legendKeyboardNavPlugin from "../plugins/LegendKeyboardNavPlugin";

import { COLOR } from "./../config";

import { changeLabelLanguageToSk } from "./../utils";

ChartJS.register(
  ...registerables,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  chartKeyboardNavPlugin,
  legendKeyboardNavPlugin,
);

type ChartComponentProps = {
  data: ChartData<"line">;
  locale: Locale;
};

const ChartComponent = memo((props: ChartComponentProps) => {
  const { data, locale } = props;

  const { localeString, currency } = localization[locale];

  const options: ChartOptions<"line"> = {
    responsive: true,
    aspectRatio: 2,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        type: "time",
        time: {
          tooltipFormat: "dd MMM, yyyy",
        },
        title: {
          display: true,
        },
        grid: {
          display: false,
        },
        ticks:{
          callback: function (tickValue){
            if(locale === 4) return changeLabelLanguageToSk(typeof tickValue === "string" ? tickValue : tickValue.toString());
            return tickValue;
          }
        }
      },
      y: {
        title: {
          display: true,
        },
        ticks: {
          // Format currency on the y-axis
          callback: function (tickValue: string | number) {
            return new Intl.NumberFormat(localeString, {
              style: "currency",
              currency,
            }).format(typeof tickValue === "string" ? parseInt(tickValue) : tickValue);
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          // Format currency inside the tooltip
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null)
              label += new Intl.NumberFormat(localeString, {
                style: "currency",
                currency,
              }).format(context.parsed.y);
            return `  ${label}`;
          },
          title: function (context){
            if(locale === 4) return changeLabelLanguageToSk(context[0].label);
            return context[0].label;
          }
        },
        // Tooltip visual customization
        enabled: true,
        backgroundColor: COLOR.grey11,
        borderColor: COLOR.grey8,
        borderWidth: 1,
        titleColor: COLOR.grey9,
        bodyColor: COLOR.grey9,
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 14 },
      },
      legend: {
        position: "bottom",
        },
    },
  };

  return (
    <Line
      tabIndex={0}
      options={options}
      data={data}
      plugins={[
        {
          // Vertical tooltip line (mouse hover)
          id: "custom_tooltip_vertical_line",
          afterDraw: (chart) => {
            if (chart.tooltip!.getActiveElements().length! <= 0) return; // eslint-disable-line @typescript-eslint/no-non-null-assertion
            if (chart.tooltip === undefined) return;
            const x = chart.tooltip.caretX;
            const yAxis = chart.scales.y;
            const { ctx } = chart;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, yAxis.top);
            ctx.lineTo(x, yAxis.bottom);
            ctx.lineWidth = 1;
            ctx.strokeStyle = COLOR.grey10;
            ctx.stroke();
            ctx.restore();
          },
        }]}
    />
  );
});

export default ChartComponent;
